import React from "react";

import { MediaUpload } from "../BuilderUtils";
import { CONTEXT_KINDS } from "../contextKinds";
import {
  CONTEXT_LAYOUT_INFO,
  COMPARE_MIN_ITEMS,
  CLEARED_LEGACY_KEYS,
  contentForLayout,
  contextGaps,
  detectMediaType,
  normalizeContextContent,
  slotsOf,
} from "../../explanation_pages/contextLayouts";

/**
 * LE CONTENU D'UNE PAGE DE CONTEXTE : d'abord une forme, ensuite ce qu'on y met.
 *
 * CE QU'IL REMPLACE. Trois cases à cocher indépendantes — « seulement le
 * média », « afficher du texte », « avec audio » — et une liste déroulante
 * photo/vidéo. Elles décrivaient le rendu champ par champ, ne nommaient jamais
 * la page obtenue, et laissaient composer des écrans qui n'existent pas : les
 * décocher toutes les trois réservait la moitié droite de l'écran pour n'y
 * rien mettre. Elles posaient aussi deux questions dont la réponse était déjà
 * là : « faut-il afficher le texte » à quelqu'un qui vient de l'écrire, et
 * « est-ce une photo ou une vidéo » à propos d'un fichier qu'il vient de
 * déposer.
 *
 * L'ORDRE COMPTE. Le format en premier, parce qu'il décide de tout le reste —
 * les emplacements affichés en dessous sont ceux, et seulement ceux, que la
 * forme choisie possède. Il n'y a donc plus de champ qui ne servirait à rien,
 * ni de contenu saisi qui ne s'afficherait nulle part.
 *
 * TOUTE MODIFICATION RÉÉCRIT LA PAGE DANS LE MODÈLE COURANT (cf.
 * `CLEARED_LEGACY_KEYS`) : une page ouverte ici en ressort sans ses anciens
 * drapeaux, plutôt que de porter deux descriptions concurrentes de son contenu.
 */
export default function ContextContentInspector({ page, onPatchContent, ctx }) {
  const { token } = ctx;
  const raw = page.content || {};
  const content = normalizeContextContent(raw);
  const slots = slotsOf(content.layout);
  const info = CONTEXT_LAYOUT_INFO[content.layout];
  const gaps = contextGaps(raw);

  const patch = (fields) => onPatchContent({ ...CLEARED_LEGACY_KEYS, ...fields });

  const changeLayout = (layout) => {
    if (layout === content.layout) return;
    onPatchContent(contentForLayout(raw, layout));
  };

  // Le format du média se déduit de l'adresse, à chaque fois qu'elle change :
  // c'est ce qui permet de n'avoir aucune liste déroulante à ce sujet.
  const setMedia = (url) => patch({ media_url: url, media_type: detectMediaType(url) });

  const setItem = (index, fields) =>
    patch({
      items: content.items.map((item, i) => (i === index ? { ...item, ...fields } : item)),
    });

  const addItem = () =>
    patch({ items: [...content.items, { media_url: "", media_type: "photo", caption: "" }] });

  const removeItem = (index) =>
    patch({ items: content.items.filter((unused, i) => i !== index) });

  return (
    <>
      <div className="builder-label-text">Format</div>
      <div className="step-picker-grid context-format-grid">
        {CONTEXT_KINDS.map((kind) => (
          <button
            key={kind.id}
            type="button"
            className={`step-picker-card${content.layout === kind.id ? " is-active" : ""}`}
            onClick={() => changeLayout(kind.id)}
            aria-pressed={content.layout === kind.id}
            title={kind.hint}
          >
            {kind.sketch}
            <span className="step-picker-label">{kind.label}</span>
          </button>
        ))}
      </div>
      <p className="step-editor-section-hint">{info.hint}</p>

      {/* CE QUI MANQUE, DIT ICI ET PAS EN SÉANCE. Un emplacement vide se voit
          aussi sur l'aperçu (encadré en pointillés), mais un auteur qui règle
          le fond ou les suites ne le regarde pas à ce moment-là. */}
      {gaps.length > 0 && (
        <div className="builder-warning-inline">
          ⚠ Cette page est incomplète : {gaps.join(", ")}.
        </div>
      )}

      <div className="builder-form-grid" style={{ marginTop: 16 }}>
        {slots.media && (
          <div style={{ gridColumn: "1 / -1" }}>
            <MediaUpload
              label={slots.media === "photo" ? "Image plein cadre" : "Image ou vidéo"}
              accept={slots.media === "photo" ? "image" : "media"}
              value={content.media_url}
              onChange={setMedia}
              token={token}
            />
            {slots.media !== "photo" && content.media_url && (
              <p className="step-editor-section-hint" style={{ marginTop: 4 }}>
                Reconnu comme {content.media_type === "video" ? "une vidéo" : "une image"}.
              </p>
            )}
          </div>
        )}

        {slots.text && (
          <label className="builder-label" style={{ gridColumn: "1 / -1" }}>
            {info.textLabel}
            <textarea
              className="builder-textarea"
              rows={content.layout === "compare" ? 2 : 6}
              value={content.text}
              onChange={(e) => patch({ text: e.target.value })}
              placeholder="Laisser vide pour ne rien afficher…"
            />
          </label>
        )}

        {slots.items > 0 && (
          <div style={{ gridColumn: "1 / -1" }}>
            <div className="builder-label-text">Visuels comparés</div>
            {content.items.map((item, index) => (
              <div className="context-compare-item" key={index}>
                <div className="builder-section-header">
                  <span className="builder-label-text">Visuel {index + 1}</span>
                  {content.items.length > COMPARE_MIN_ITEMS && (
                    <button
                      type="button"
                      className="builder-btn-secondary small"
                      onClick={() => removeItem(index)}
                    >
                      Retirer
                    </button>
                  )}
                </div>
                <MediaUpload
                  label=""
                  accept="media"
                  value={item.media_url}
                  onChange={(url) => setItem(index, { media_url: url, media_type: detectMediaType(url) })}
                  token={token}
                />
                <input
                  className="builder-input"
                  style={{ marginTop: 6 }}
                  value={item.caption}
                  onChange={(e) => setItem(index, { caption: e.target.value })}
                  placeholder="Légende (ex : « Avant la mesure »)"
                />
              </div>
            ))}
            {content.items.length < slots.items && (
              <button type="button" className="builder-btn-secondary" onClick={addItem}>
                + Ajouter un visuel
              </button>
            )}
          </div>
        )}

        {slots.audio && (
          <div style={{ gridColumn: "1 / -1" }}>
            <MediaUpload
              label="Bande son (facultative)"
              accept="audio"
              value={content.audio_url}
              onChange={(url) => patch({ audio_url: url })}
              token={token}
            />
          </div>
        )}
      </div>
    </>
  );
}
